import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-slate-200 bg-white scrollbar-thin',
        'dark:bg-slate-900 dark:border-slate-800',
        className,
      )}
    >
      <table className="w-full text-sm table-zebra">{children}</table>
    </div>
  )
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide dark:bg-slate-900/50 dark:text-slate-400">
      {children}
    </thead>
  )
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{children}</tbody>
}

export function TR({
  children,
  onClick,
  selected,
  className,
}: {
  children: ReactNode
  onClick?: () => void
  selected?: boolean
  className?: string
}) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        onClick && 'cursor-pointer hover:bg-brand-50/40 dark:hover:bg-slate-800/60',
        selected && 'bg-brand-50 dark:bg-slate-800',
        className,
      )}
    >
      {children}
    </tr>
  )
}

interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center'
}

export function TH({ children, align = 'left', className, ...props }: ThProps) {
  return (
    <th
      {...props}
      className={cn(
        'px-3 py-2 font-medium text-slate-600 dark:text-slate-400',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  )
}

interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center'
  numeric?: boolean
}

export function TD({ children, align, numeric, className, ...props }: TdProps) {
  return (
    <td
      {...props}
      className={cn(
        'px-3 py-2 text-slate-800 dark:text-slate-200',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        numeric && 'num text-right',
        className,
      )}
    >
      {children}
    </td>
  )
}
